<script setup lang="ts">
import { ref, computed, nextTick, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import type { FieldPart } from "@/types/field-observations.types";
import {
  useGetFieldParts,
  useCreateFieldPart,
  useUpdateFieldPart,
} from "@/stores/queries/field-observations.query";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import InsertPartTemplateDialog from "./InsertPartTemplateDialog.vue";
import { buildPartNumbering } from "@/utils/field-parts.utils";

/**
 * Arborescence des parties d'ouvrage aux couleurs du canvas — brique
 * commune de la modal du sélecteur de partie (`PartPickerLine`) et de
 * l'onglet Parties du volet Observations. Tout est déplié par défaut
 * (collapse par nœud), numérotation « 1.2.1 » sur l'arbre complet.
 *
 * Ajout de parties : un « + » permanent devant chaque ligne (utilisable au
 * doigt, pas de survol requis) ouvre un menu au-dessus / en dessous /
 * sous-partie / renommer / insérer un modèle. La position est persistée via
 * `FieldPart.order` (Float) : insérer entre deux voisins = prendre le milieu
 * de leurs deux valeurs, sans renumérotation. Les parties antérieures à ce
 * champ valent toutes 0 : la fratrie est alors renumérotée une fois, dans
 * son ordre d'affichage courant.
 *
 * Slots : `row-extra` (à droite du titre, reçoit `part`) et `after-row`
 * (sous la ligne, reçoit `part`) — l'onglet Parties y pose la liste des
 * observations rattachées.
 */

const props = defineProps<{
  diagnosticId: number;
  modelValue?: number;
  /** Compteur affiché à droite de chaque partie (ses observations). */
  counts?: Map<number, number>;
  /**
   * Posé en ligne dans un volet (pas dans une modal) : le volet défile, pas
   * l'arbre ; pas de pied « insérer un modèle » ni de coche de sélection —
   * l'appelant pose ses propres actions (cf. `addRoot` / `openTemplate`).
   */
  inline?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: number | undefined): void;
}>();

const { t } = useTranslation();
const { data: parts } = useGetFieldParts(toRef(props, "diagnosticId"));
const { mutateAsync: createPart } = useCreateFieldPart();
const { mutateAsync: updatePart } = useUpdateFieldPart();

/** Frères d'un parent, dans l'ordre d'affichage (celui renvoyé par l'API). */
const siblingsOf = (parentId: number | null): FieldPart[] =>
  (parts.value ?? []).filter((p) => (p.parentId ?? null) === parentId);

const collapsed = ref<Set<number>>(new Set());

interface TreeRow {
  part: FieldPart;
  depth: number;
  hasChildren: boolean;
}

const treeRows = computed<TreeRow[]>(() => {
  const rows: TreeRow[] = [];
  const walk = (parentId: number | null, depth: number) => {
    for (const part of siblingsOf(parentId)) {
      const children = siblingsOf(part.id);
      rows.push({ part, depth, hasChildren: children.length > 0 });
      if (children.length && !collapsed.value.has(part.id)) {
        walk(part.id, depth + 1);
      }
    }
  };
  walk(null, 0);
  return rows;
});

/** « 1.2.1 » par partie, calculé sur l'arbre complet (cf. buildPartNumbering). */
const numbering = computed(() => buildPartNumbering(parts.value));

const toggleCollapse = (partId: number) => {
  const next = new Set(collapsed.value);
  if (next.has(partId)) next.delete(partId);
  else next.add(partId);
  collapsed.value = next;
};

const selectPart = (partId: number) => {
  emit("update:modelValue", partId);
};

// ── Ajout de partie ─────────────────────────────────────────────────────────

type InsertPosition = "above" | "below" | "child" | "root";

interface InsertTarget {
  /** Emplacement du champ de saisie : `<position>-<id>` ou `root`. */
  key: string;
  position: InsertPosition;
  /** Parent de la future partie. */
  parentId?: number;
  /** Partie de référence pour au-dessus / en dessous. */
  targetPart?: FieldPart;
  depth: number;
}

const insertTarget = ref<InsertTarget | null>(null);
const newPartName = ref("");
const isCreating = ref(false);

/**
 * Ref-fonction : un `ref` nommé posé dans un `v-for` est collecté par Vue sous
 * forme de tableau (même avec un seul élément rendu) — le focus échouerait.
 * On focalise directement au montage.
 */
const focusInsertInput = (el: Element | null) => {
  if (el) (el as HTMLInputElement).focus();
};

const openInsert = (target: InsertTarget) => {
  cancelRename();
  insertTarget.value = target;
  newPartName.value = "";
};

const addAbove = (row: TreeRow) =>
  openInsert({
    key: `above-${row.part.id}`,
    position: "above",
    parentId: row.part.parentId ?? undefined,
    targetPart: row.part,
    depth: row.depth,
  });

const addBelow = (row: TreeRow) =>
  openInsert({
    key: `below-${row.part.id}`,
    position: "below",
    parentId: row.part.parentId ?? undefined,
    targetPart: row.part,
    depth: row.depth,
  });

/** Sous-partie : la branche est dépliée pour que la saisie soit visible. */
const addChild = (row: TreeRow) => {
  if (collapsed.value.has(row.part.id)) toggleCollapse(row.part.id);
  openInsert({
    key: `child-${row.part.id}`,
    position: "child",
    parentId: row.part.id,
    depth: row.depth + 1,
  });
};

const addRoot = () => openInsert({ key: "root", position: "root", depth: 0 });

const cancelInsert = () => {
  insertTarget.value = null;
  newPartName.value = "";
};

/**
 * Garantit des `order` distincts et croissants dans la fratrie (les parties
 * créées avant ce champ valent toutes 0) : renumérote 1..n dans l'ordre
 * d'affichage courant, une seule fois.
 * @returns les `order` de la fratrie, alignés sur `siblings`.
 */
const ensureDistinctOrders = async (
  siblings: FieldPart[],
): Promise<number[]> => {
  const orders = siblings.map((s) => s.order ?? 0);
  const isStrictlyIncreasing = orders.every(
    (value, i) => i === 0 || orders[i - 1] < value,
  );
  if (isStrictlyIncreasing) return orders;

  const renumbered = siblings.map((_, i) => i + 1);
  await Promise.all(
    siblings.map((sibling, i) =>
      updatePart({
        id: sibling.id,
        payload: { order: renumbered[i] },
        diagnosticId: props.diagnosticId,
      }),
    ),
  );
  return renumbered;
};

/** Ordre de la nouvelle partie : milieu entre la cible et son voisin. */
const computeInsertOrder = async (target: InsertTarget): Promise<number> => {
  if (target.position === "child" || target.position === "root") {
    const siblings = siblingsOf(target.parentId ?? null);
    const max = siblings.reduce((acc, s) => Math.max(acc, s.order ?? 0), 0);
    return max + 1;
  }

  const siblings = siblingsOf(target.parentId ?? null);
  const orders = await ensureDistinctOrders(siblings);
  const index = siblings.findIndex((s) => s.id === target.targetPart?.id);
  if (index < 0) return orders.length + 1;

  if (target.position === "above") {
    const previous = index > 0 ? orders[index - 1] : orders[index] - 2;
    return (previous + orders[index]) / 2;
  }
  const next =
    index < orders.length - 1 ? orders[index + 1] : orders[index] + 2;
  return (orders[index] + next) / 2;
};

/**
 * Vrai le temps d'un enchaînement (Entrée) : le champ courant se démonte pour
 * laisser place au suivant, et son `blur` ne doit pas être pris pour une sortie
 * de saisie — sinon la chaîne se refermerait aussitôt.
 */
const isChaining = ref(false);

/**
 * @param chain Entrée : enchaîne une nouvelle saisie sous la partie créée, pour
 * dérouler une liste de parties sans repasser par le menu « + ».
 */
const submitInsert = async (chain = false) => {
  // Entrée puis blur au démontage : sans ce garde, la partie serait créée 2 fois.
  if (isCreating.value || !insertTarget.value) return;
  const target = insertTarget.value;
  const name = newPartName.value.trim();
  if (!name) {
    cancelInsert();
    return;
  }
  isCreating.value = true;
  isChaining.value = chain;
  try {
    const order = await computeInsertOrder(target);
    const created = await createPart({
      name,
      diagnosticId: props.diagnosticId,
      parentId: target.parentId,
      order,
    });

    if (chain && created) {
      // La partie suivante se place sous celle qu'on vient de créer : c'est elle
      // la nouvelle référence, sinon les suivantes s'empileraient à l'envers.
      newPartName.value = "";
      insertTarget.value = {
        key: `below-${created.id}`,
        position: "below",
        parentId: target.parentId,
        targetPart: created,
        depth: target.depth,
      };
    } else {
      cancelInsert();
    }
  } finally {
    isCreating.value = false;
    await nextTick();
    isChaining.value = false;
  }
};

/** Sortie du champ = fin de saisie, sauf pendant un enchaînement. */
const onInsertBlur = () => {
  if (isChaining.value) return;
  submitInsert();
};

/**
 * Décalage horizontal du champ de saisie, pour aligner le texte sur le titre
 * des parties : padding de ligne + bouton « + » + chevron (22px + 4px chacun).
 */
const insertOffset = (depth: number): string => `${depth * 20 + 56}px`;

// ── Renommage en place ──────────────────────────────────────────────────────

const renamingId = ref<number | null>(null);
const renameValue = ref("");
const isRenaming = ref(false);

const startRename = (part: FieldPart) => {
  cancelInsert();
  renamingId.value = part.id;
  renameValue.value = part.name;
};

const cancelRename = () => {
  renamingId.value = null;
  renameValue.value = "";
};

const submitRename = async () => {
  if (isRenaming.value || renamingId.value === null) return;
  const id = renamingId.value;
  const name = renameValue.value.trim();
  const current = parts.value?.find((p) => p.id === id);
  if (!name || !current || name === current.name) {
    cancelRename();
    return;
  }
  isRenaming.value = true;
  try {
    await updatePart({
      id,
      payload: { name },
      diagnosticId: props.diagnosticId,
    });
  } finally {
    isRenaming.value = false;
    cancelRename();
  }
};

// ── Insertion d'une arborescence enregistrée ────────────────────────────────
// Même modal que l'outil terrain : on ne redéveloppe pas la sélection de modèle.

const showTemplateModal = ref(false);
/** Parent sous lequel greffer le modèle ; absent = à la racine. */
const templateParentId = ref<number | undefined>();

const openTemplate = (parentId?: number) => {
  cancelInsert();
  templateParentId.value = parentId;
  showTemplateModal.value = true;
};

/** Le modèle peut arriver sous une branche fermée : on la déplie. */
const onTemplateInserted = () => {
  if (templateParentId.value && collapsed.value.has(templateParentId.value)) {
    toggleCollapse(templateParentId.value);
  }
};

defineExpose({ addRoot, openTemplate });
</script>

<template>
  <!-- Aucune partie : amorce centrée (ensuite, tout passe par les « + ») -->
  <div v-if="!treeRows.length" class="text-center py-3">
    <p class="text-grey mb-2">{{ t("canvas:observations.part.empty") }}</p>
    <div v-if="insertTarget?.key === 'root'" class="insert-slot px-3">
      <input
        :ref="(el) => focusInsertInput(el as Element | null)"
        v-model="newPartName"
        class="insert-input"
        :placeholder="t('canvas:observations.part.namePlaceholder')"
        :disabled="isCreating"
        @keydown.enter.prevent="submitInsert(true)"
        @keydown.esc.prevent="cancelInsert"
        @blur="onInsertBlur"
      />
    </div>
    <div v-else-if="!inline" class="d-flex justify-content-center gap-2 flex-wrap">
      <button class="add-root-btn" @click="addRoot">
        <PhosphorIcon name="plus" :size="13" weight="bold" />
        {{ t("canvas:observations.part.addRoot") }}
      </button>
      <button class="add-root-btn" @click="openTemplate()">
        <PhosphorIcon name="folder-open" :size="13" weight="bold" />
        {{ t("canvas:observations.part.insertTemplate") }}
      </button>
    </div>
  </div>

  <div v-else class="part-tree">
    <div :class="inline ? 'tree-inline' : 'tree-scroll'">
      <template v-for="row in treeRows" :key="row.part.id">
        <!-- Saisie « au-dessus » -->
        <div
          v-if="insertTarget?.key === `above-${row.part.id}`"
          class="insert-slot"
          :style="{ paddingLeft: insertOffset(insertTarget.depth) }"
        >
          <input
            :ref="(el) => focusInsertInput(el as Element | null)"
            v-model="newPartName"
            class="insert-input"
            :placeholder="t('canvas:observations.part.namePlaceholder')"
            :disabled="isCreating"
            @keydown.enter.prevent="submitInsert(true)"
            @keydown.esc.prevent="cancelInsert"
            @blur="onInsertBlur"
          />
        </div>

        <div
          class="tree-row d-flex align-items-center gap-1 rounded"
          :class="{ selected: row.part.id === modelValue }"
          :style="{ paddingLeft: `${row.depth * 20 + 4}px` }"
        >
          <!-- Menu d'ajout (permanent : utilisable au doigt) -->
          <el-dropdown trigger="click" @click.stop>
            <button class="add-btn" @click.stop>
              <PhosphorIcon name="plus" :size="12" weight="bold" />
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="addAbove(row)">
                  <span class="d-inline-flex align-items-center gap-2">
                    <PhosphorIcon name="arrow-up" :size="14" />
                    {{ t("canvas:observations.part.addAbove") }}
                  </span>
                </el-dropdown-item>
                <el-dropdown-item @click="addBelow(row)">
                  <span class="d-inline-flex align-items-center gap-2">
                    <PhosphorIcon name="arrow-down" :size="14" />
                    {{ t("canvas:observations.part.addBelow") }}
                  </span>
                </el-dropdown-item>
                <el-dropdown-item divided @click="addChild(row)">
                  <span class="d-inline-flex align-items-center gap-2">
                    <PhosphorIcon name="tree-structure" :size="14" />
                    {{ t("canvas:observations.part.addChild") }}
                  </span>
                </el-dropdown-item>
                <el-dropdown-item divided @click="startRename(row.part)">
                  <span class="d-inline-flex align-items-center gap-2">
                    <PhosphorIcon name="pencil-simple" :size="14" />
                    {{ t("canvas:observations.part.rename") }}
                  </span>
                </el-dropdown-item>
                <el-dropdown-item divided @click="openTemplate(row.part.id)">
                  <span class="d-inline-flex align-items-center gap-2">
                    <PhosphorIcon name="folder-open" :size="14" />
                    {{ t("canvas:observations.part.insertTemplateHere") }}
                  </span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <button
            v-if="row.hasChildren"
            class="collapse-btn d-flex align-items-center justify-content-center"
            @click.stop="toggleCollapse(row.part.id)"
          >
            <PhosphorIcon
              :name="collapsed.has(row.part.id) ? 'caret-right' : 'caret-down'"
              :size="12"
              weight="bold"
            />
          </button>
          <span v-else class="collapse-spacer" />

          <!-- Renommage en place -->
          <input
            v-if="renamingId === row.part.id"
            :ref="(el) => focusInsertInput(el as Element | null)"
            v-model="renameValue"
            class="insert-input flex-grow-1 my-1"
            :disabled="isRenaming"
            @keydown.enter.prevent="submitRename"
            @keydown.esc.prevent="cancelRename"
            @blur="submitRename"
          />
          <span
            v-else
            class="flex-grow-1 py-1 part-name text-truncate"
            role="button"
            @click="selectPart(row.part.id)"
          >
            <span class="part-number">{{ numbering.get(row.part.id) }}</span>
            {{ row.part.name }}
          </span>
          <slot name="row-extra" :part="row.part" />
          <span
            v-if="counts && (counts.get(row.part.id) ?? 0) > 0"
            class="count-badge"
          >
            {{ counts.get(row.part.id) }}
          </span>
          <PhosphorIcon
            v-if="row.part.id === modelValue && !inline"
            name="check-circle"
            weight="fill"
            :size="16"
            class="text-primary me-2"
          />
        </div>

        <slot name="after-row" :part="row.part" />

        <!-- Saisie « en dessous » ou « sous-partie » -->
        <div
          v-if="
            insertTarget &&
            (insertTarget.key === `below-${row.part.id}` ||
              insertTarget.key === `child-${row.part.id}`)
          "
          class="insert-slot"
          :style="{ paddingLeft: insertOffset(insertTarget.depth) }"
        >
          <input
            :ref="(el) => focusInsertInput(el as Element | null)"
            v-model="newPartName"
            class="insert-input"
            :placeholder="t('canvas:observations.part.namePlaceholder')"
            :disabled="isCreating"
            @keydown.enter.prevent="submitInsert(true)"
            @keydown.esc.prevent="cancelInsert"
            @blur="onInsertBlur"
          />
        </div>
      </template>
    </div>

    <!-- Insertion d'une arborescence enregistrée, au niveau racine -->
    <div v-if="!inline" class="tree-footer">
      <button class="add-root-btn" @click="openTemplate()">
        <PhosphorIcon name="folder-open" :size="13" weight="bold" />
        {{ t("canvas:observations.part.insertTemplate") }}
      </button>
    </div>
  </div>

  <InsertPartTemplateDialog
    v-model="showTemplateModal"
    :diagnostic-id="diagnosticId"
    :parent-part-id="templateParentId"
    @inserted="onTemplateInserted"
  />
</template>

<style lang="scss" scoped>
// Ce composant ne vit que dans l'outil dessin (modal téléportée hors du volet
// sombre, ou volet lui-même) : les tons sont posés en dur plutôt que via les
// jetons Bootstrap, qui résoudraient au thème clair de l'app.

.tree-row {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);

  &:hover {
    background-color: rgba(255, 255, 255, 0.06);
  }

  &.selected {
    background-color: rgba(255, 255, 255, 0.12);
  }
}

/* L'arbre peut être long : il défile au lieu de faire grandir la modal au-delà
   de l'écran. Le scroll ne porte que sur les lignes, pour que le pied
   « insérer un modèle » reste toujours atteignable. */
.tree-scroll {
  max-height: min(55vh, 460px);
  overflow-y: auto;
}

.part-name {
  cursor: pointer;
  min-width: 0;
}

/* Même typo que le nom de la partie : le numéro fait partie du titre. */
.part-number {
  font-variant-numeric: tabular-nums;
  margin-right: 4px;
}

.count-badge {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.12);
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  margin-right: 6px;
  flex-shrink: 0;
}

.collapse-btn {
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.45);
  padding: 0;

  &:hover {
    color: #fff;
  }
}

.collapse-spacer {
  width: 22px;
}

// ── Ajout de partie ─────────────────────────────────────────────────────────
.add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border: none;
  border-radius: var(--bs-border-radius);
  background-color: transparent;
  color: rgba(255, 255, 255, 0.6);
  padding: 0;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--canvas-btn-active-bg);
      color: #1a1a1a;
    }
  }
}

/* Aplat discret plutôt qu'un contour pointillé : le pointillé sur fond sombre
   se lit comme une zone désactivée. */
.add-root-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: var(--bs-border-radius);
  background-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.8rem;
  padding: 6px 12px;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.16);
    color: #fff;
  }
}

.tree-footer {
  display: flex;
  justify-content: center;
  padding-top: 10px;
  margin-top: 6px;
  border-top: 1px solid var(--canvas-border);
}

.insert-slot {
  display: flex;
  align-items: center;
  padding-top: 2px;
  padding-bottom: 2px;
}

.insert-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid var(--canvas-btn-active-bg);
  background: transparent;
  color: #fff;
  font-size: 0.9rem;
  padding: 0 2px;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
    font-size: 0.8rem;
  }
}
</style>
